import React, {useEffect, useState} from "react";
import {jwtDecode} from "jwt-decode";
import tokenStorage from '../../utils/tokenStorage';
import RegistrationRequest from "../../components/Authentication/RegistrationRequest";
import EmailLogin from "../../components/Authentication/EmailLogin";
import GuestLogin from "../../components/Authentication/GuestLogin";
import ExternalLogin from "../../components/Authentication/ExternalLogin";

import "../../components/Authentication/style.scss";

const getNameField = ($locale, moduleAuth) => {
    let label = $locale.get('auth.login.email.name');
    let placeholder = $locale.get('registration.placeholder.name');

    const customLabel = $locale.getProperty(moduleAuth, 'guestNicknamePlaceholder');
    if (customLabel) {
        label = customLabel;
        placeholder = null;
    }

    return {
        label,
        type: 'text',
        name: 'name',
        required: true,
        placeholder,
    };
}

const AuthenticationBlock = ({
    preview,
    previewComponent,
    eventKey,
    eventName,
    eventPremoderation,
    timeStart,
    customDescription,
    $locale,
    loginWithEmailEnabled,
    loginWithNameEnabled,
    loginWithPhoneEnabled,
    externalServices,
    ssoIconUrl,
    registrationFields,
    participationType,
    onWindowResize = () => {},
    onLogin = () => {},
    onLoginSuccess = () => {},
    backgroundColor = '',
    moduleSettings = {},
    moduleAuth = {},
    authRequired = false,
    hideInfoBlock = false,
    corporateMail = false,
    participationFormatLabels = [],
    registrationType = 'hybrid',
}) => {
    const [client, setClient] = useState(null);
    const [isRegistered, setRegistered] = useState(false);
    const fields = [getNameField($locale, moduleAuth), ...registrationFields]
    const internalLoginEnabled = loginWithEmailEnabled || loginWithNameEnabled;

    /** Флаг, определяющий, является ли SSO единственным доступным методом авторизации. */
    const isSsoAuthOnly = !(internalLoginEnabled || loginWithPhoneEnabled) && externalServices.length === 1 && externalServices.includes('sso');

    useEffect(() => {
        const fieldsRequired = registrationFields.filter(field => field.required).map(field => field.name);

        const tokenStorageListener = () => {
            try {
                const payload = jwtDecode(tokenStorage.get());

                if (payload?.clientRegistrationId ?? payload?.id) {
                    setClient((loginWithEmailEnabled && !payload.email) ? null : payload);

                    if (onLoginSuccess && payload.info && ((moduleAuth.anonymous && payload.info?.type === "anonymous") || fieldsRequired.every(field => payload.info[field]))) {
                        onLoginSuccess(payload);
                    }
                    return onLogin ? onLogin(payload) : null;
                }
            } catch (e) {
                console.error(e)
            }
            setClient(null);
            return onLogin ? onLogin(null) : null;
        };

        tokenStorage.setEventKey(eventKey);
        tokenStorage.addEventListener(tokenStorageListener);
        tokenStorageListener();
    }, []);

    useEffect(() => {
        if (onWindowResize) {
            onWindowResize();
        }
    }, [client]);

    return (
        <div
            style={{backgroundColor: backgroundColor}}
            className="auth-container registration"
        >
            {client && !preview
                ? <RegistrationRequest
                    preview={preview}
                    $locale={$locale}
                    eventKey={eventKey}
                    allowAnonymousLogin={!moduleAuth?.require && moduleAuth?.anonymous}
                    participationType={participationType}
                    registrationType={registrationType}
                    client={!preview && client}
                    fields={fields}
                    onWindowResize={onWindowResize}
                />
                : <>
                    {(loginWithEmailEnabled || loginWithPhoneEnabled) && (
                        <div className="auth-container">
                            <EmailLogin
                                preview={preview}
                                previewComponent={previewComponent}
                                $locale={$locale}
                                eventKey={eventKey}
                                eventName={eventName}
                                timeStart={timeStart}
                                moduleAuth={moduleAuth}
                                authRequired={authRequired}
                                loginWithPhoneEnabled={loginWithPhoneEnabled}
                                eventPremoderation={eventPremoderation}
                                moduleSettings={moduleSettings}
                                customDescription={customDescription}
                                registrationFields={fields}
                                participationType={participationType}
                                hideInfoBlock={hideInfoBlock}
                                corporateMail={corporateMail}
                                participationFormatLabels={participationFormatLabels}
                                externalServices={externalServices}
                                ssoIconUrl={ssoIconUrl}
                                internalLoginEnabled={internalLoginEnabled}
                                registrationType={registrationType}
                                onRegister={setRegistered}
                                onWindowResize={onWindowResize}
                            />
                        </div>
                    )}
                    {loginWithNameEnabled && (
                        <div className="auth-container">
                            <GuestLogin
                                $locale={$locale}
                                eventKey={eventKey}
                                moduleAuth={moduleAuth}
                                eventName={eventName}
                                registrationFields={fields}
                                participationType={participationType}
                                authRequired={authRequired}
                                hideLoginSubtitle={moduleSettings.hideLoginSubtitle}
                                customDescription={customDescription}
                                hideInfoBlock={hideInfoBlock}
                                onWindowResize={onWindowResize}
                                registrationType={registrationType}
                                participationFormatLabels={participationFormatLabels}
                            />
                        </div>
                    )}
                    {externalServices?.length > 0 && (
                        <ExternalLogin
                            $locale={$locale}
                            eventKey={eventKey}
                            eventName={eventName}
                            timeStart={timeStart}
                            internalLoginEnabled={internalLoginEnabled}
                            isSsoAuthOnly={isSsoAuthOnly}
                            services={externalServices}
                            ssoIconUrl={ssoIconUrl}
                            onWindowResize={onWindowResize}
                            participationType={participationType}
                        />
                    )}
                </>
            }
        </div>
    );
};

export default AuthenticationBlock;
